import { styled } from "@mui/material/styles";
import { PencilIcon, SearchField, Tag, TextArea, thinScrollbar } from "@UI";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { isAtMax, isOverridden, matchesFilter } from "../utils/choiceModel";
const FREE_TEXT_MAX_ROWS = 4;
const Root = styled("div")({
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "var(--j-sp-2)",
    "&[data-dense=true]": {
        flex: "1 1 auto",
        minHeight: 0
    },
    "& > .question": {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "var(--j-sp-1-5)"
    },
    "& .question-text": {
        fontWeight: 500,
        overflowWrap: "anywhere",
        outline: "none"
    },
    "& > .hook-meta": {
        marginBottom: 0
    },
    "& > .hook-meta:empty": {
        display: "none"
    }
});
const Options = styled("div")({
    display: "flex",
    flexDirection: "column",
    margin: "0 calc(-1 * var(--j-sp-2))",
    transition: "opacity var(--j-dur-fade) var(--j-ease)",
    "&[data-overridden=true]": {
        opacity: 0.5
    },
    "&[data-dense=true]": {
        flex: "1 1 auto",
        minHeight: "calc(3 * var(--j-row))",
        margin: "0 calc(-1 * var(--j-sp-3))",
        overflowY: "auto",
        overscrollBehavior: "contain",
        borderTop: "1px solid var(--j-line-soft)",
        borderBottom: "1px solid var(--j-line-soft)",
        background: [
            "linear-gradient(var(--j-surface) 30%, rgb(var(--j-surface-rgb) / 0)) center top / 100% 1.5rem no-repeat local",
            "linear-gradient(rgb(var(--j-surface-rgb) / 0), var(--j-surface) 70%) center bottom / 100% 1.5rem no-repeat local",
            "radial-gradient(farthest-side at 50% 0, rgb(var(--j-shadow-rgb) / .14), transparent) center top / 100% .5rem no-repeat scroll",
            "radial-gradient(farthest-side at 50% 100%, rgb(var(--j-shadow-rgb) / .14), transparent) center bottom / 100% .5rem no-repeat scroll"
        ].join(", "),
        backgroundColor: "var(--j-surface)",
        ...thinScrollbar
    },
    "& > .empty": {
        margin: 0,
        padding: "var(--j-sp-1-5) var(--j-sp-3)",
        fontSize: "var(--j-fs-sm)",
        color: "var(--j-ink-3)"
    }
});
const Option = styled("label")({
    display: "flex",
    alignItems: "flex-start",
    gap: "var(--j-sp-2)",
    padding: "var(--j-sp-1-5) var(--j-sp-2)",
    borderRadius: "var(--j-r-sm)",
    cursor: "pointer",
    minWidth: 0,
    "&:hover": {
        background: "var(--j-hover)"
    },
    "&[data-checked=true]": {
        background: "var(--j-accent-soft)"
    },
    "&[data-disabled=true]": {
        cursor: "default"
    },
    "&[data-disabled=true][data-checked=false]": {
        color: "var(--j-ink-3)",
        "& .option-description": {
            color: "var(--j-ink-3)"
        },
        "&:hover": {
            background: "none"
        }
    },
    "& > .option-text": {
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        overflowWrap: "anywhere"
    },
    "& .option-description": {
        fontSize: "var(--j-fs-sm)",
        lineHeight: "var(--j-lh-sm)",
        color: "var(--j-ink-2)"
    },
    "& > input": {
        appearance: "none",
        WebkitAppearance: "none",
        margin: "2px 0 0",
        width: "0.875rem",
        height: "0.875rem",
        flexShrink: 0,
        display: "grid",
        placeItems: "center",
        border: "1px solid var(--j-line-cb)",
        background: "var(--j-surface)",
        cursor: "inherit",
        transition: "background-color 150ms, border-color 150ms, border-width 150ms",
        "&:focus-visible": {
            outline: "var(--j-focus-w) solid var(--j-accent)",
            outlineOffset: "var(--j-focus-offset)"
        },
        "&:disabled:not(:checked)": {
            opacity: 0.4
        }
    },
    "& > input[type=radio]": {
        borderRadius: "50%"
    },
    "& > input[type=radio]:checked": {
        borderWidth: "0.25rem",
        borderColor: "var(--j-accent)"
    },
    "& > input[type=checkbox]": {
        borderRadius: "var(--j-r-xs)"
    },
    "& > input[type=checkbox]:checked": {
        background: "var(--j-accent)",
        borderColor: "var(--j-accent)"
    },
    "& > input[type=checkbox]:checked::after": {
        content: '""',
        width: 7,
        height: 4,
        border: "solid var(--j-on-accent)",
        borderWidth: "0 0 2px 2px",
        transform: "translateY(-1px) rotate(-45deg)"
    },
    "[data-dense=true] > &": {
        minHeight: "var(--j-row)",
        flexShrink: 0,
        alignItems: "center",
        padding: "0 var(--j-sp-3)",
        borderRadius: 0,
        "& > input": {
            marginTop: 0
        },
        "& > .option-text": {
            flexDirection: "row",
            alignItems: "baseline",
            gap: "var(--j-sp-2)"
        },
        "& .option-label, & .option-description": {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
        },
        "& .option-label": {
            flexShrink: 0,
            maxWidth: "100%"
        },
        "& .option-description": {
            flex: 1,
            minWidth: 0,
            textAlign: "right",
            fontSize: "var(--j-fs-xs)",
            color: "var(--j-ink-3)"
        }
    }
});
const FreeText = styled("div")({
    display: "flex",
    alignItems: "flex-start",
    gap: "var(--j-sp-2)",
    flexShrink: 0,
    "& > svg": {
        marginTop: 7,
        color: "var(--j-ink-3)"
    },
    "& > textarea": {
        flex: 1,
        minWidth: 0
    }
});
function metaOf(question, draft, t) {
    if (isOverridden(draft)) return t("chat.ask.overridden");
    const values = {
        max: question.max,
        count: draft.optionIds.length
    };
    if (question.multiSelect && question.max) {
        return t(isAtMax(question, draft.optionIds) ? "chat.ask.atMax" : "chat.ask.chooseUpTo", values);
    }
    return question.multiSelect ? t("chat.ask.chooseAll") : "";
}
export function QuestionSet({ name, question, draft, isBatch, isDense, isDisabled, onPick, onText, onEnter }) {
    const { t } = useTranslation();
    const [filter, setFilter] = useState("");
    const isPointer = useRef(true);
    const textId = `${name}-text`;
    const isMaxed = isAtMax(question, draft.optionIds);
    const visible = question.options.filter((option)=>!isDense || matchesFilter(option, filter));
    const onKeyDown = (event)=>{
        isPointer.current = false;
        if (!isDense || event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
        const target = event.target;
        const root = event.currentTarget;
        const rows = Array.from(root.querySelectorAll("label > input"));
        const search = root.querySelector("input[type=search]");
        const index = rows.indexOf(target);
        const next = target === search ? event.key === "ArrowDown" && rows[0] : index >= 0 && (event.key === "ArrowDown" ? rows[index + 1] : rows[index - 1] ?? search);
        if (!next) return;
        event.preventDefault();
        next.focus();
        next.closest("label")?.scrollIntoView({
            block: "nearest"
        });
    };
    return /*#__PURE__*/ React.createElement(Root, {
        role: "group",
        "aria-labelledby": isBatch ? textId : undefined,
        "data-dense": isDense,
        onKeyDown: onKeyDown,
        onPointerDownCapture: ()=>{
            isPointer.current = true;
        }
    }, isBatch && /*#__PURE__*/ React.createElement("div", {
        className: "question"
    }, question.header && /*#__PURE__*/ React.createElement(Tag, {
        tone: "grey"
    }, question.header), /*#__PURE__*/ React.createElement("span", {
        className: "question-text",
        id: textId,
        tabIndex: -1
    }, question.question)), isDense && /*#__PURE__*/ React.createElement(SearchField, {
        value: filter,
        onChange: setFilter,
        disabled: isDisabled,
        placeholder: t("chat.ask.filter", {
            count: question.options.length
        }),
        "aria-label": t("chat.ask.filterLabel")
    }), /*#__PURE__*/ React.createElement(Options, {
        "data-dense": isDense,
        "data-overridden": isOverridden(draft)
    }, visible.map((option)=>{
        const isChecked = draft.optionIds.includes(option.id);
        const isOptionDisabled = isDisabled || isMaxed && !isChecked;
        return /*#__PURE__*/ React.createElement(Option, {
            key: option.id,
            "data-checked": isChecked,
            "data-disabled": isOptionDisabled
        }, /*#__PURE__*/ React.createElement("input", {
            type: question.multiSelect ? "checkbox" : "radio",
            name: name,
            value: option.id,
            checked: isChecked,
            disabled: isOptionDisabled,
            onChange: ()=>onPick(option.id, isPointer.current)
        }), /*#__PURE__*/ React.createElement("span", {
            className: "option-text"
        }, /*#__PURE__*/ React.createElement("span", {
            className: "option-label"
        }, option.label), option.description && /*#__PURE__*/ React.createElement("span", {
            className: "option-description"
        }, option.description)));
    }), isDense && visible.length === 0 && /*#__PURE__*/ React.createElement("p", {
        className: "empty"
    }, t("chat.ask.noMatches", {
        term: filter.trim()
    }))), /*#__PURE__*/ React.createElement(FreeText, null, /*#__PURE__*/ React.createElement(PencilIcon, {
        size: 14
    }), /*#__PURE__*/ React.createElement(TextArea, {
        minRows: 1,
        maxRows: FREE_TEXT_MAX_ROWS,
        value: draft.customText,
        disabled: isDisabled,
        "aria-label": question.freeTextLabel ?? t("chat.ask.freeTextLabel"),
        placeholder: question.freeTextPlaceholder ?? t("chat.ask.freeTextPlaceholder"),
        onChange: (event)=>onText(event.target.value),
        onKeyDown: (event)=>{
            if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
            event.preventDefault();
            onEnter();
        }
    })), /*#__PURE__*/ React.createElement("p", {
        className: "hook-meta",
        "aria-live": "polite"
    }, metaOf(question, draft, t)));
}
